import { Vector3, type PerspectiveCamera } from "three/webgpu";
import { blackHoleConfig } from "../config/blackHoleConfig";
import { WORLD_ORIGIN } from "../config/worldFrame";

const HIT_SCALE = 1.08;
const MIN_DETERMINANT = 0.0001;

export class SingularityInteractionProjector {
  private readonly centerNdc = new Vector3();
  private readonly xNdc = new Vector3();
  private readonly zNdc = new Vector3();
  private readonly xPoint = new Vector3();
  private readonly zPoint = new Vector3();

  contains(
    camera: PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
    screenX: number,
    screenY: number,
    singularityPosition: Vector3,
  ) {
    this.centerNdc.copy(singularityPosition).project(camera);
    if (this.centerNdc.z < -1 || this.centerNdc.z > 1) return false;

    this.xPoint.copy(WORLD_ORIGIN).setX(blackHoleConfig.diskOuterRadius).add(singularityPosition);
    this.zPoint.copy(WORLD_ORIGIN).setZ(blackHoleConfig.diskOuterRadius).add(singularityPosition);
    this.xNdc.copy(this.xPoint).project(camera);
    this.zNdc.copy(this.zPoint).project(camera);

    const centerX = (this.centerNdc.x * 0.5 + 0.5) * viewportWidth;
    const centerY = (-this.centerNdc.y * 0.5 + 0.5) * viewportHeight;
    const axisXX = (this.xNdc.x - this.centerNdc.x) * viewportWidth * 0.5 * HIT_SCALE;
    const axisXY = -(this.xNdc.y - this.centerNdc.y) * viewportHeight * 0.5 * HIT_SCALE;
    const axisZX = (this.zNdc.x - this.centerNdc.x) * viewportWidth * 0.5 * HIT_SCALE;
    const axisZY = -(this.zNdc.y - this.centerNdc.y) * viewportHeight * 0.5 * HIT_SCALE;
    const determinant = axisXX * axisZY - axisZX * axisXY;
    if (Math.abs(determinant) < MIN_DETERMINANT) return false;

    const dx = screenX - centerX;
    const dy = screenY - centerY;
    const a = (dx * axisZY - axisZX * dy) / determinant;
    const b = (axisXX * dy - dx * axisXY) / determinant;
    return a * a + b * b <= 1;
  }
}
